import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { PropTypes } from '../../../utils/propTypes';
import styles from './Tabs2.module.css';

/**
 * Tabs (2.0) — layered tab panels, reimplemented in Athena's idiom (JSX + CSS
 * Module + 2.0 tokens). Flat + mode-invariant (segmented bg var(--muted),
 * active var(--background); line underline var(--primary)). Lives in Tabs2/
 * because the 1.0 `Tabs/` folder is the legacy TabNavigation. Preview-only.
 *
 *   <Tabs defaultValue="a">
 *     <TabsList><TabsTrigger value="a">A</TabsTrigger>…</TabsList>
 *     <TabsContent value="a">…</TabsContent>
 *   </Tabs>
 */

const TabsContext = createContext(null);
const useTabs = () => {
  const ctx = useContext(TabsContext);
  if (!ctx) throw new Error('Tabs sub-components must be used within <Tabs>');
  return ctx;
};

const Tabs = ({
  value: controlledValue,
  defaultValue = null,
  onValueChange,
  orientation = 'horizontal',
  className = '',
  children,
  ...props
}) => {
  const isControlled = controlledValue !== undefined;
  const [internal, setInternal] = useState(defaultValue);
  const value = isControlled ? controlledValue : internal;

  const setValue = useCallback(
    (next) => {
      if (!isControlled) setInternal(next);
      onValueChange?.(next);
    },
    [isControlled, onValueChange]
  );

  const ctx = useMemo(() => ({ value, setValue, orientation }), [value, setValue, orientation]);

  return (
    <TabsContext.Provider value={ctx}>
      <div
        data-orientation={orientation}
        className={`${styles.tabs} ${orientation === 'vertical' ? styles.vertical : ''} ${className}`}
        {...props}
      >
        {children}
      </div>
    </TabsContext.Provider>
  );
};

const TabsList = ({ variant = 'default', className = '', children, ...props }) => {
  const { orientation } = useTabs();
  return (
    <div
      role="tablist"
      aria-orientation={orientation}
      data-variant={variant}
      className={`${styles.list} ${variant === 'line' ? styles.listLine : styles.listSolid} ${className}`}
      {...props}
    >
      {children}
    </div>
  );
};

const TabsTrigger = ({ value, disabled = false, className = '', children, ...props }) => {
  const { value: selected, setValue } = useTabs();
  const active = selected === value;

  const onKeyDown = (e) => {
    if (!['ArrowRight', 'ArrowLeft', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(e.key)) return;
    e.preventDefault();
    const list = e.currentTarget.closest('[role="tablist"]');
    if (!list) return;
    const tabs = Array.from(list.querySelectorAll('[role="tab"]:not(:disabled)'));
    const idx = tabs.indexOf(e.currentTarget);
    let next;
    if (e.key === 'Home') next = tabs[0];
    else if (e.key === 'End') next = tabs[tabs.length - 1];
    else {
      const dir = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : -1;
      next = tabs[(idx + dir + tabs.length) % tabs.length];
    }
    if (next) {
      next.focus();
      next.click();
    }
  };

  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      tabIndex={active ? 0 : -1}
      data-state={active ? 'active' : 'inactive'}
      disabled={disabled}
      className={`${styles.trigger} ${className}`}
      onClick={() => !disabled && setValue(value)}
      onKeyDown={onKeyDown}
      {...props}
    >
      {children}
    </button>
  );
};

const TabsContent = ({ value, className = '', children, ...props }) => {
  const { value: selected } = useTabs();
  if (selected !== value) return null;
  return (
    <div role="tabpanel" className={`${styles.content} ${className}`} {...props}>
      {children}
    </div>
  );
};

Tabs.propTypes = {
  value: PropTypes.string,
  defaultValue: PropTypes.string,
  onValueChange: PropTypes.func,
  orientation: PropTypes.string,
  className: PropTypes.string,
  children: PropTypes.node,
};
TabsList.propTypes = { variant: PropTypes.string, className: PropTypes.string, children: PropTypes.node };
TabsTrigger.propTypes = {
  value: PropTypes.string,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  children: PropTypes.node,
};
TabsContent.propTypes = { value: PropTypes.string, className: PropTypes.string, children: PropTypes.node };

export default Tabs;
export { Tabs, TabsList, TabsTrigger, TabsContent };
